import supabase from '../config/supabase';
import { colors } from '../theme/colors';
import type { PaginatedResponse } from '../types';
import type { EffortLevel, ExerciseItem, ExerciseLookup, ExerciseLookupsResponse, ExerciseMethodLookup, ExerciseRecord, ExerciseType } from '../types/exercise';
import { handleServiceError } from '../utils/errorHandler';
import {
  DEFAULT_CONCENTRIC_SECONDS,
  DEFAULT_ECCENTRIC_SECONDS,
  DEFAULT_PAUSE_SECONDS,
  isCadenceExecutionSpeed,
  normalizeExecutionSpeed,
} from '../utils/executionSpeed';
import { insertStorageFileByPath } from './storageFiles';
import { requestEdgeFunction } from './edgeFunctionClient';

export interface CreateExercisePayload {
  name: string;
  code: string | null;
  muscle_group_ids: number[];
  resistance_type_id: number | null;
  instructions: string | null;
  default_sets: string | null;
  default_repetitions_min: number | null;
  default_repetitions_max: number | null;
  default_rest_sec: number | null;
  default_execution_sec: number | null;
  default_concentric_seconds: number | null;
  default_pause_seconds: number | null;
  default_eccentric_seconds: number | null;
  default_load_percent: number | null;
  default_execution_speed: string | null;
  default_effort_level: EffortLevel | null;
  exercise_type: ExerciseType | null;
  default_method_id: number | null;
  default_heart_rate_min: number | null;
  default_heart_rate_max: number | null;
  objective: string | null;
  video_url: string | null;
}

export type UpdateExercisePayload = CreateExercisePayload;

interface PaginatedExerciseRow {
  id: number;
  name: string;
  muscle_group_ids: number[];
  group: string | null;
  resistance_type: string | null;
  objective: string | null;
  video_url: string | null;
  created_at: string;
}

interface RelatedExercisesFunctionResponse {
  data: PaginatedExerciseRow[];
}

interface ExerciseLookupsFunctionResponse {
  muscleGroups?: ExerciseLookup[];
  resistanceTypes?: ExerciseLookup[];
  methods?: Array<{
    id: number;
    name: string;
    name_en?: string | null;
    type: string;
    color: string | null;
    text_color?: string | null;
  }>;
}

export interface ListExercisesFilters {
  searchTerm?: string;
  groupIds?: string[];
  exerciseTypes?: ExerciseType[];
  resistanceTypeIds?: string[];
}

const LOOKUPS_FUNCTION_NAME = 'list-exercise-lookups';
const PAGINATED_EXERCISES_FUNCTION_NAME = 'list-paginated-exercises';
const RELATED_EXERCISES_FUNCTION_NAME = 'manage-related-exercises';
const EXERCISE_THUMBNAIL_BUCKET = 'exercises-photos';
const EXERCISE_THUMBNAIL_BUCKET_ALTERNATIVE = 'exercices-photos';
const EXERCISE_THUMBNAIL_BUCKET_CANDIDATES = [
  EXERCISE_THUMBNAIL_BUCKET,
  EXERCISE_THUMBNAIL_BUCKET_ALTERNATIVE,
] as const;
const THUMBNAIL_FILE_NAME = 'thumbnail';
const THUMBNAIL_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'] as const;
const PRIMARY_THUMBNAIL_EXTENSION = 'jpg';
const MAX_THUMBNAIL_SIZE_BYTES = 60 * 1024;
const MAX_THUMBNAIL_DIMENSION = 1600;
const MIN_THUMBNAIL_DIMENSION = 320;

const thumbnailUrlCache = new Map<number, string | null>();
let resolvedThumbnailBucket: string | null = null;

const isBucketNotFoundError = (error: unknown): boolean => {
  const statusCode = (error as { statusCode?: string | number } | null)?.statusCode;
  const message = `${(error as { message?: string } | null)?.message ?? ''}`.toLowerCase();

  return statusCode === 404 || statusCode === '404' || message.includes('bucket not found');
};

const getThumbnailBucketCandidates = (): string[] => {
  if (!resolvedThumbnailBucket) {
    return [...EXERCISE_THUMBNAIL_BUCKET_CANDIDATES];
  }

  return [
    resolvedThumbnailBucket,
    ...EXERCISE_THUMBNAIL_BUCKET_CANDIDATES.filter((bucket) => bucket !== resolvedThumbnailBucket),
  ];
};

const buildThumbnailPath = (exerciseId: number, extension: string) => (
  `${exerciseId}/${THUMBNAIL_FILE_NAME}.${extension}`
);

const loadImageElement = (file: File): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  const objectUrl = URL.createObjectURL(file);

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Não foi possível carregar a imagem selecionada.'));
  };

  image.src = objectUrl;
});

const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob> => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Não foi possível processar a imagem selecionada.'));
      return;
    }

    resolve(blob);
  }, mimeType, quality);
});

const fitDimensions = (width: number, height: number, maxDimension: number) => {
  const largestSide = Math.max(width, height);

  if (largestSide <= maxDimension) {
    return { width, height };
  }

  const scale = maxDimension / largestSide;

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const processThumbnailToMaxSize = async (file: File): Promise<File> => {
  const image = await loadImageElement(file);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Não foi possível preparar a compressão da imagem.');
  }

  let targetDimension = Math.min(MAX_THUMBNAIL_DIMENSION, Math.max(image.width, image.height));

  while (targetDimension >= MIN_THUMBNAIL_DIMENSION) {
    const { width, height } = fitDimensions(image.width, image.height, targetDimension);
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    for (const quality of [0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.65, 0.6, 0.55, 0.5, 0.45, 0.4]) {
      const blob = await canvasToBlob(canvas, 'image/jpeg', quality);

      if (blob.size <= MAX_THUMBNAIL_SIZE_BYTES) {
        return new File([blob], `${THUMBNAIL_FILE_NAME}.jpg`, { type: 'image/jpeg' });
      }
    }

    targetDimension = Math.floor(targetDimension * 0.85);
  }

  throw new Error('Não foi possível reduzir a imagem para no máximo 60 KB. Selecione uma imagem menor.');
};

const buildPublicThumbnailUrl = (exerciseId: number, extension: string, bucketName: string = EXERCISE_THUMBNAIL_BUCKET) => {
  const { data } = supabase.storage
    .from(bucketName)
    .getPublicUrl(buildThumbnailPath(exerciseId, extension));

  return data.publicUrl;
};

const buildAccessibleThumbnailUrl = async (
  exerciseId: number,
  extension: string,
  bucketName: string,
): Promise<string> => {
  const thumbnailPath = buildThumbnailPath(exerciseId, extension);
  const { data, error } = await supabase.storage
    .from(bucketName)
    .createSignedUrl(thumbnailPath, 60 * 60 * 24 * 7);

  if (!error && data?.signedUrl) {
    return data.signedUrl;
  }

  return buildPublicThumbnailUrl(exerciseId, extension, bucketName);
};

const getExerciseThumbnailUrl = (exerciseId: number): string => (
  thumbnailUrlCache.get(exerciseId) ?? buildPublicThumbnailUrl(exerciseId, PRIMARY_THUMBNAIL_EXTENSION, EXERCISE_THUMBNAIL_BUCKET)
);

const getExerciseThumbnailCandidates = (exerciseId: number): string[] => {
  const candidateUrls = [
    thumbnailUrlCache.get(exerciseId) ?? null,
    ...EXERCISE_THUMBNAIL_BUCKET_CANDIDATES.flatMap((bucketName) => THUMBNAIL_EXTENSIONS.map((extension) => (
      buildPublicThumbnailUrl(exerciseId, extension, bucketName)
    ))),
  ].filter((url): url is string => Boolean(url));

  return Array.from(new Set(candidateUrls));
};

const resolveStoredThumbnailUrl = async (exerciseId: number): Promise<string | null> => {
  if (thumbnailUrlCache.has(exerciseId)) {
    return thumbnailUrlCache.get(exerciseId) ?? null;
  }

  for (const bucketName of getThumbnailBucketCandidates()) {
    const { data: files, error } = await supabase.storage
      .from(bucketName)
      .list(String(exerciseId), { limit: 100 });

    if (error) {
      if (isBucketNotFoundError(error)) {
        continue;
      }

      handleServiceError(error, 'carregar thumbnail do exercício');
    }

    const fileNames = (files ?? []).map((file) => file.name.toLowerCase());

    for (const extension of THUMBNAIL_EXTENSIONS) {
      const fileName = `${THUMBNAIL_FILE_NAME}.${extension}`;

      if (fileNames.includes(fileName)) {
        const publicUrl = await buildAccessibleThumbnailUrl(exerciseId, extension, bucketName);
        resolvedThumbnailBucket = bucketName;
        thumbnailUrlCache.set(exerciseId, publicUrl);
        return publicUrl;
      }
    }
  }

  thumbnailUrlCache.set(exerciseId, null);
  return null;
};

const buildExerciseFallbackThumbnail = (name: string): string => {
  const safeName = name.trim() || 'Exercício';
  const initials = safeName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="144" height="144" viewBox="0 0 144 144" fill="none">
      <rect width="144" height="144" rx="12" fill="${colors.exerciseThumbnailFallbackBg}"/>
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="${colors.exerciseThumbnailFallbackText}" font-family="Roboto, Arial, sans-serif" font-size="32" font-weight="700">${initials || 'EX'}</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const mapExerciseRecordToItem = (
  record: ExerciseRecord,
  muscleGroupsMap: Map<number, string>,
  resistanceTypesMap: Map<number, string>,
): ExerciseItem => ({
  id: record.id,
  code: record.code,
  name: record.name,
  muscleGroupId: record.muscle_group_id ?? null,
  muscleGroupIds: record.muscle_group_id ? [record.muscle_group_id] : [],
  group: record.muscle_group_id ? muscleGroupsMap.get(record.muscle_group_id) ?? 'Não informado' : 'Não informado',
  resistanceTypeId: record.resistance_type_id,
  resistanceType: record.resistance_type_id ? resistanceTypesMap.get(record.resistance_type_id) ?? 'Sem tipo de resistência informado' : 'Sem tipo de resistência informado',
  objective: record.objective ?? 'Sem objetivo informado.',
  instructions: record.instructions ?? '',
  videoUrl: record.video_url ?? '',
  createdAt: record.created_at,
  thumbnail: getExerciseThumbnailUrl(record.id),
  defaultExecutionSpeed: normalizeExecutionSpeed(record.default_execution_speed),
  defaultExecutionSeconds: record.default_execution_sec ?? null,
  defaultConcentricSeconds: record.default_concentric_seconds ?? null,
  defaultPauseSeconds: record.default_pause_seconds ?? null,
  defaultEccentricSeconds: record.default_eccentric_seconds ?? null,
  exerciseType: record.exercise_type ?? null,
});

const parseLegacyRepetitionRange = (value?: string | null) => {
  const matches = value?.match(/\d+/g) ?? [];

  if (matches.length === 0) {
    return { min: '', max: '' };
  }

  if (matches.length === 1) {
    return { min: matches[0] ?? '', max: matches[0] ?? '' };
  }

  return { min: matches[0] ?? '', max: matches[1] ?? '' };
};

const formatOptionalSeconds = (value: number | string | null | undefined) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }

  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
    return String(Number.parseInt(value, 10));
  }

  return '';
};

const formatCadenceSecondsField = (
  value: number | string | null | undefined,
  fallback: number,
  enabled: boolean,
) => {
  const formattedValue = formatOptionalSeconds(value);

  if (formattedValue !== '') {
    return formattedValue;
  }

  return enabled ? String(fallback) : '';
};

const mapExerciseRecordToFormValues = (record: ExerciseRecord, muscleGroupIds: number[] = []) => {
  const legacyRepetitionRange = parseLegacyRepetitionRange(record.default_reps);
  const executionSpeed = normalizeExecutionSpeed(record.default_execution_speed) ?? '';
  const usesCadenceSpeed = isCadenceExecutionSpeed(executionSpeed);

  return {
    name: record.name,
    muscleGroupIds: muscleGroupIds.map(String),
    resistanceTypeId: record.resistance_type_id ? String(record.resistance_type_id) : '',
    objective: record.objective ?? '',
    description: record.instructions ?? '',
    executionTime: formatOptionalSeconds(record.default_execution_sec),
    restTime: formatOptionalSeconds(record.default_rest_sec),
    heartRate: record.default_heart_rate_max != null ? String(record.default_heart_rate_max) : '',
    sets: record.default_sets != null ? String(record.default_sets) : '',
    repetitionsMin: record.default_repetitions_min != null ? String(record.default_repetitions_min) : legacyRepetitionRange.min,
    repetitionsMax: record.default_repetitions_max != null ? String(record.default_repetitions_max) : legacyRepetitionRange.max,
    method: record.default_method_id ? String(record.default_method_id) : '',
    loadPercentage: record.default_load_percent != null ? String(record.default_load_percent) : '',
    executionSpeed,
    concentricSeconds: formatCadenceSecondsField(
      record.default_concentric_seconds,
      DEFAULT_CONCENTRIC_SECONDS,
      usesCadenceSpeed,
    ),
    pauseSeconds: formatCadenceSecondsField(
      record.default_pause_seconds,
      DEFAULT_PAUSE_SECONDS,
      usesCadenceSpeed,
    ),
    eccentricSeconds: formatCadenceSecondsField(
      record.default_eccentric_seconds,
      DEFAULT_ECCENTRIC_SECONDS,
      usesCadenceSpeed,
    ),
    intensity: record.default_effort_level ?? '',
    exerciseType: record.exercise_type ?? '',
    videoUrl: record.video_url ?? '',
  };
};

const mapPaginatedExerciseRowToItem = (row: PaginatedExerciseRow): ExerciseItem => ({
  id: row.id,
  code: null,
  name: row.name,
  muscleGroupId: row.muscle_group_ids[0] ?? null,
  muscleGroupIds: row.muscle_group_ids ?? [],
  group: row.group ?? 'Não informado',
  resistanceTypeId: null,
  resistanceType: row.resistance_type ?? 'Sem tipo de resistência informado',
  objective: row.objective ?? 'Sem objetivo informado.',
  instructions: '',
  videoUrl: row.video_url ?? '',
  createdAt: row.created_at,
  thumbnail: getExerciseThumbnailUrl(row.id),
  defaultExecutionSpeed: null,
  defaultExecutionSeconds: null,
  defaultConcentricSeconds: null,
  defaultPauseSeconds: null,
  defaultEccentricSeconds: null,
  exerciseType: null,
});

class ExerciseServiceClass {
  getThumbnail(exerciseId: number): string {
    return getExerciseThumbnailUrl(exerciseId);
  }

  async resolveThumbnail(exerciseId: number): Promise<string | null> {
    return resolveStoredThumbnailUrl(exerciseId);
  }

  getThumbnailCandidates(exerciseId: number): string[] {
    return getExerciseThumbnailCandidates(exerciseId);
  }

  getThumbnailFallback(exerciseName: string): string {
    return buildExerciseFallbackThumbnail(exerciseName);
  }

  async getExerciseMuscleGroupIds(exerciseId: number): Promise<number[]> {
    try {
      const response = await requestEdgeFunction<{ data?: number[] }>('manage-exercise', {
        method: 'POST',
        body: {
          action: 'get-muscle-group-ids',
          payload: { exerciseId },
        },
      });
      return (response.data ?? []).filter((item): item is number => Number.isInteger(item) && item > 0);
    } catch (error) {
      handleServiceError(error, 'carregar grupos musculares do exercício');
      return [];
    }
  }

  private async fetchLookupsFromFunction(): Promise<ExerciseLookupsFunctionResponse | null> {
    return requestEdgeFunction<ExerciseLookupsFunctionResponse>(LOOKUPS_FUNCTION_NAME, {
      method: 'GET',
    }).catch(() => null);
  }

  private async invokeFunction<T>(
    functionName: string,
    options: {
      method?: 'GET' | 'POST';
      query?: Record<string, string>;
      body?: unknown;
    } = {},
  ): Promise<T> {
    return requestEdgeFunction<T>(functionName, {
      method: options.method ?? 'GET',
      query: options.query,
      body: options.body,
    }).catch((error) => {
      handleServiceError(error, 'executar operação de exercícios');
      throw error;
    });
  }

  async getExerciseLookups(): Promise<ExerciseLookupsResponse> {
    const functionLookups = await this.fetchLookupsFromFunction();
    if (!functionLookups) {
      handleServiceError(new Error('Não foi possível carregar listas de exercício.'), 'carregar listas de exercício');
    }

    return {
      muscleGroups: functionLookups?.muscleGroups ?? [],
      resistanceTypes: functionLookups?.resistanceTypes ?? [],
      methods: (functionLookups?.methods ?? []).map((method) => ({
        id: method.id,
        name: method.name,
        nameEn: method.name_en ?? null,
        type: method.type,
        color: method.color ?? null,
        textColor: method.text_color ?? null,
      })),
    };
  }

  async getMuscleGroups(): Promise<ExerciseLookup[]> {
    return (await this.getExerciseLookups()).muscleGroups;
  }

  async getResistanceTypes(): Promise<ExerciseLookup[]> {
    return (await this.getExerciseLookups()).resistanceTypes;
  }

  async getExerciseMethods(): Promise<ExerciseMethodLookup[]> {
    return (await this.getExerciseLookups()).methods;
  }

  async getEquipments(): Promise<ExerciseLookup[]> {
    return this.getResistanceTypes();
  }

  private async uploadThumbnail(exerciseId: number, file: File): Promise<void> {
    const processedFile = await processThumbnailToMaxSize(file);

    const extension = PRIMARY_THUMBNAIL_EXTENSION;
    const thumbnailPath = buildThumbnailPath(exerciseId, extension);

    for (const bucketName of getThumbnailBucketCandidates()) {
      const { error } = await supabase.storage
        .from(bucketName)
        .upload(thumbnailPath, processedFile, {
          upsert: true,
          contentType: processedFile.type || undefined,
        });

      if (!error) {
        resolvedThumbnailBucket = bucketName;
        const thumbnailUrl = await buildAccessibleThumbnailUrl(exerciseId, extension, bucketName);
        thumbnailUrlCache.set(exerciseId, thumbnailUrl);

        const { data: authData } = await supabase.auth.getUser();
        await insertStorageFileByPath({
          bucketId: bucketName,
          storagePath: thumbnailPath,
          contentType: processedFile.type || null,
          sizeBytes: processedFile.size,
          ownerType: 'exercise',
          ownerId: exerciseId,
          fileKind: 'exercise_thumbnail',
          uploadedByUserId: authData.user?.id ?? null,
        });

        return;
      }

      if (isBucketNotFoundError(error)) {
        continue;
      }

      handleServiceError(error, 'enviar thumbnail do exercício');
    }

    throw new Error('Bucket de imagens dos exercícios não encontrado no projeto Supabase.');
  }

  private async removeThumbnail(exerciseId: number): Promise<void> {
    const paths = THUMBNAIL_EXTENSIONS.map((extension) => buildThumbnailPath(exerciseId, extension));

    for (const bucketName of getThumbnailBucketCandidates()) {
      const { error } = await supabase.storage
        .from(bucketName)
        .remove(paths);

      if (!error) {
        resolvedThumbnailBucket = bucketName;
        break;
      }

      if (isBucketNotFoundError(error)) {
        continue;
      }

      handleServiceError(error, 'remover thumbnail do exercício');
    }

    thumbnailUrlCache.delete(exerciseId);
  }

  async getExercises(): Promise<ExerciseItem[]> {
    const [result, lookups] = await Promise.all([
      this.listPaginated(1, 500),
      this.getExerciseLookups(),
    ]);
    const muscleGroupsMap = new Map(lookups.muscleGroups.map((item) => [item.id, item.name]));
    const resistanceTypesMap = new Map(lookups.resistanceTypes.map((item) => [item.id, item.name]));
    const records = result.data as unknown as ExerciseRecord[];

    return records.map((record) => (
      mapExerciseRecordToItem(record, muscleGroupsMap, resistanceTypesMap)
    ));
  }

  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: ListExercisesFilters = {},
  ): Promise<PaginatedResponse<ExerciseItem>> {
    const result = await requestEdgeFunction<PaginatedResponse<PaginatedExerciseRow>>(PAGINATED_EXERCISES_FUNCTION_NAME, {
      method: 'GET',
      query: {
        page,
        pageSize,
        search: filters.searchTerm?.trim() || undefined,
        muscle_group_ids: filters.groupIds?.length ? filters.groupIds : undefined,
        exercise_types: filters.exerciseTypes?.length ? filters.exerciseTypes : undefined,
        resistance_type_ids: filters.resistanceTypeIds?.length ? filters.resistanceTypeIds : undefined,
      },
    }).catch((error) => {
      handleServiceError(error, 'listar exercícios');
      throw error;
    });

    return {
      pagination: result.pagination,
      data: result.data.map((row) => mapPaginatedExerciseRowToItem(row)),
    };
  }

  async getExerciseById(exerciseId: number): Promise<ExerciseRecord> {
    const response = await requestEdgeFunction<{ data: ExerciseRecord }>('manage-exercise', {
      method: 'POST',
      body: {
        action: 'get-by-id',
        payload: { exerciseId },
      },
    }).catch((error) => {
      handleServiceError(error, 'carregar exercício');
      throw error;
    });

    return response.data;
  }

  async getRelatedExercises(primaryExerciseId: number): Promise<ExerciseItem[]> {
    const response = await this.invokeFunction<RelatedExercisesFunctionResponse>(RELATED_EXERCISES_FUNCTION_NAME, {
      method: 'GET',
      query: {
        primary_exercise_id: String(primaryExerciseId),
      },
    });

    return response.data.map((row) => mapPaginatedExerciseRowToItem(row));
  }

  async syncRelatedExercises(primaryExerciseId: number, relatedExerciseIds: number[]): Promise<ExerciseItem[]> {
    const response = await this.invokeFunction<RelatedExercisesFunctionResponse>(RELATED_EXERCISES_FUNCTION_NAME, {
      method: 'POST',
      body: {
        primaryExerciseId,
        relatedExerciseIds,
      },
    });

    return response.data.map((row) => mapPaginatedExerciseRowToItem(row));
  }

  async getExerciseEditorData(exerciseId: number): Promise<{ record: ExerciseRecord; muscleGroupIds: number[]; relatedExercises: ExerciseItem[] }> {
    const [record, muscleGroupIds, relatedExercises] = await Promise.all([
      this.getExerciseById(exerciseId),
      this.getExerciseMuscleGroupIds(exerciseId),
      this.getRelatedExercises(exerciseId),
    ]);

    return { record, muscleGroupIds, relatedExercises };
  }

  getFormValuesFromRecord(record: ExerciseRecord, muscleGroupIds: number[] = []) {
    return mapExerciseRecordToFormValues(record, muscleGroupIds);
  }

  async createExercise(payload: CreateExercisePayload, thumbnailFile?: File | null, relatedExerciseIds: number[] = []): Promise<ExerciseRecord> {
    try {
      const response = await requestEdgeFunction<{ data: ExerciseRecord }>('manage-exercise', {
        method: 'POST',
        body: {
          action: 'create',
          payload: { record: payload },
        },
      });

      const createdExercise = response.data;

      await this.syncRelatedExercises(createdExercise.id, relatedExerciseIds);

      if (thumbnailFile) {
        await this.uploadThumbnail(createdExercise.id, thumbnailFile);
      }

      return createdExercise;
    } catch (error) {
      handleServiceError(error, 'salvar exercício');
    }
  }

  async updateExercise(exerciseId: number, payload: UpdateExercisePayload, thumbnailFile?: File | null, relatedExerciseIds: number[] = []): Promise<ExerciseRecord> {
    try {
      if (thumbnailFile) {
        await this.uploadThumbnail(exerciseId, thumbnailFile);
      }

      const response = await requestEdgeFunction<{ data: ExerciseRecord }>('manage-exercise', {
        method: 'POST',
        body: {
          action: 'update',
          payload: { exerciseId, record: payload },
        },
      });

      await this.syncRelatedExercises(exerciseId, relatedExerciseIds);

      return response.data;
    } catch (error) {
      handleServiceError(error, 'atualizar exercício');
    }
  }

  async deleteExercise(exerciseId: number): Promise<void> {
    await requestEdgeFunction('manage-exercise', {
      method: 'POST',
      body: {
        action: 'deactivate',
        payload: { exerciseId },
      },
    }).catch((error) => {
      handleServiceError(error, 'inativar exercício');
      throw error;
    });
  }
}

export const ExerciseService = new ExerciseServiceClass();